<template>
  <div>
    <!-- Single Resource Management Modal -->
    <b-modal 
      v-model="showModal" 
      :title="`Manage ${title} Resources`" 
      size="lg"
      @show="onModalShow"
      @hidden="resetAll"
    >
      <b-tabs v-model="outerTab" content-class="mt-3">
        <!-- Files Tab -->
        <b-tab title="Files">
          <b-tabs v-model="activeFileTab" content-class="mt-2">
            <b-tab title="Existing Files" active>
              <b-alert v-if="!existingFiles.length" show variant="info">
                No files uploaded for this {{ title }} yet.
              </b-alert>
              <b-table 
                v-else
                :items="existingFiles" 
                :fields="fileFields"
                striped hover small responsive
              >
                <template #cell(upload_date)="data">
                  {{ formatDate(data.value) }}
                </template>
                <template #cell(actions)="row">
                  <base-button size="sm" type="danger" @click="$emit('delete-file', row.item)">
                    Delete
                  </base-button>
                </template>
              </b-table>
            </b-tab>
            <b-tab title="Upload New File">
              <file-loader
                initialLabel="Select file to upload"
                accept="*/*"
                :maxSize="10000000"
                :parse-json="false"
                :raw-bytes="true"
                @file-loaded="onFileLoaded"
              />
              <b-form-group label="Device Path" class="mt-3">
                <b-form-input
                  v-model="devicePath"
                  placeholder="e.g., updates/app_update.bin"
                />
                <small class="text-muted">Path on target device</small>
              </b-form-group>
            </b-tab>
          </b-tabs>
        </b-tab>

        <!-- Firmware Tab -->
        <b-tab title="Firmware">
          <b-tabs v-model="activeFirmwareTab" content-class="mt-2">
            <b-tab title="Installed Firmware" active>
              <b-alert v-if="!existingFirmware.length" show variant="info">
                No firmware uploaded for this {{ title }} yet.
              </b-alert>
              <b-table 
                v-else
                :items="existingFirmware" 
                :fields="firmwareFields"
                striped hover small responsive
              >
                <template #cell(release_date)="data">
                  {{ formatDate(data.value) }}
                </template>
                <template #cell(actions)="row">
                  <base-button size="sm" type="danger" @click="$emit('delete-firmware', row.item)">
                    Delete
                  </base-button>
                </template>
              </b-table>
            </b-tab>
            <b-tab title="Upload New Firmware">
              <file-loader
                initialLabel="Select firmware file"
                accept="*/*"
                :maxSize="10000000"
                :parse-json="false"
                :raw-bytes="true"
                @file-loaded="onFirmwareFileLoaded"
              />
            
              <b-form-group label="Version" class="mt-3">
  <b-form-input v-model="firmwareVersion" placeholder="e.g., 1.0.17-rel" required />
  <small class="text-muted">Format: major.minor.patch-tag (tag: rel, beta, rc, dev)</small>
 <!-- <div v-if="firmwareVersion && !isFirmwareVersionValid" class="text-danger small mt-1">
    Invalid format. Use e.g., 1.0.17-rel
  </div> -->
</b-form-group>
              <b-form-group label="Device Type">
                <b-form-select v-model="firmwareDeviceType" :options="deviceTypeOptions" required />
              </b-form-group>
              <b-form-group label="Description">
                <b-form-input v-model="firmwareDescription" placeholder="Brief description" />
              </b-form-group>
              <b-form-group label="WiFi Firmware File (Optional)" class="mt-3">
  <file-loader
    initialLabel="Select wifi_update.bin (optional)"
    accept="*/*"
    :maxSize="10000000"
    :parse-json="false"
    :raw-bytes="true"
    @file-loaded="onWifiFirmwareFileLoaded"
  />
</b-form-group>
            </b-tab>
          </b-tabs>
        </b-tab>

        <!-- Icons Tab -->
        <b-tab title="Icons">
          <b-tabs v-model="activeIconTab" content-class="mt-2">
            <b-tab title="Existing Icons" active>
              <b-alert v-if="!existingIcons.length" show variant="info">
                No icons uploaded for this {{ title }} yet.
              </b-alert>
              <b-table 
                v-else
                :items="existingIcons" 
                :fields="iconFields"
                striped hover small responsive
              >
                <template #cell(upload_date)="data">
                  {{ formatDate(data.value) }}
                </template>
                <template #cell(format)="data">
                  <b-badge variant="info">{{ data.value }}</b-badge>
                </template>
                <template #cell(actions)="row">
                  <base-button size="sm" type="danger" @click="$emit('delete-icon', row.item)">
                    Delete
                  </base-button>
                </template>
              </b-table>
            </b-tab>
            <b-tab title="Upload New Icon">
              <b-alert show variant="info" class="mb-3">
                <small>Uploaded images will be automatically converted to PBM format</small>
                <small>The sizing of the original image should be the desired outpzt size</small>

              </b-alert>
              <file-loader
                initialLabel="Select icon image (PNG, JPG, etc.)"
                accept="image/*"
                :maxSize="5000000"
                :parse-json="false"
                :raw-bytes="true"
                @file-loaded="onIconFileLoaded"
              />
              <b-form-group label="File Name" class="mt-3">
                <b-form-input v-model="iconName" placeholder="e.g., menu (without .pbm)" />
                <small class="text-muted">The .pbm extension will be added automatically. Device path will be <code>imgs/pbm/menu.pbm</code></small>
              </b-form-group>
              <b-form-group label="Description (Optional)">
                <b-form-input v-model="iconDescription" placeholder="Brief description" />
              </b-form-group>
            </b-tab>
          </b-tabs>
        </b-tab>
      </b-tabs>

      <!-- Modal Footer -->
      <template #modal-footer>
        <base-button type="secondary" @click="showModal = false">Close</base-button>
        <!-- Dynamic action button based on outer tab and inner tab -->
        <base-button
          v-if="outerTab === 0 && activeFileTab === 1"
          type="primary"
          @click="uploadFile"
          :disabled="!fileToUpload || uploading"
        >
          <span v-if="!uploading">Upload File</span>
          <span v-else>Uploading...</span>
        </base-button>
        <base-button
          v-if="outerTab === 1 && activeFirmwareTab === 1"
          type="primary"
          @click="uploadFirmware"
          :disabled="!canUploadFirmware || firmwareUploading"
        >
          <span v-if="!firmwareUploading">Upload Firmware</span>
          <span v-else>Uploading...</span>
        </base-button>
        <base-button
          v-if="outerTab === 2 && activeIconTab === 1"
          type="primary"
          @click="uploadIcon"
          :disabled="!iconFile || !iconName.trim() || iconUploading"
        >
          <span v-if="!iconUploading">Upload & Convert Icon</span>
          <span v-else>Converting & Uploading...</span>
        </base-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import FileLoader from "@/views/components/FileLoader";

export default {
  name: "UploadModals",
  components: { FileLoader },
  props: {
    title: { type: String, default: "Entity" },
    id: { type: String, required: true },
    siteId: { type: String, default: null },
    existingFiles: { type: Array, default: () => [] },
    existingFirmware: { type: Array, default: () => [] },
    existingIcons: { type: Array, default: () => [] }
  },
  data() {
    return {
      // Modal visibility and tabs
      showModal: false,
      outerTab: 0, // 0: Files, 1: Firmware, 2: Icons

      // File tab state
      activeFileTab: 0,
      fileToUpload: null,
      devicePath: "",
      uploading: false,
      fileFields: [
        { key: 'filename', label: 'File Name' },
        { key: 'file_device_path', label: 'Device Path' },
        { key: 'upload_date', label: 'Upload Date' },
        { key: 'size_bytes', label: 'Size' },
        { key: 'actions', label: 'Actions' }
      ],

      // Firmware tab state
      activeFirmwareTab: 0,
      firmwareFile: null,
      firmwareVersion: '',
      firmwareDescription: '',
      wifiFirmwareFile: null,
      firmwareUploading: false,
      firmwareFields: [
        { key: 'version', label: 'Version' },
        { key: 'device_type', label: 'Device Type' },
        { key: 'filename', label: 'File Name' },
        { key: 'release_date', label: 'Upload Date' },
        { key: 'actions', label: 'Actions' }
      ],
      deviceTypeOptions: [
        { value: 'CC1', text: 'CC1' },
        { value: 'CC2', text: 'CC2' }
      ],
      firmwareDeviceType: 'CC2',

      // Icon tab state
      activeIconTab: 0,
      iconFile: null,
      iconName: "",
      iconDescription: "",
      iconUploading: false,
      iconFields: [
        { key: 'filename', label: 'Original Name' },
        { key: 'converted_filename', label: 'Converted Name' },
        { key: 'format', label: 'Format' },
        { key: 'file_device_path', label: 'Device Path' },
        { key: 'upload_date', label: 'Upload Date' },
        { key: 'size_bytes', label: 'Size' },
        { key: 'actions', label: 'Actions' }
      ]
    };
  },
  watch: {
  outerTab(newVal, oldVal) {
    // Emit only when switching *to* the Firmware tab
    if (newVal === 1 && oldVal !== 1) {
      this.$emit('firmware-tab-activated');
    }
  }
},
  computed: {
    canUploadFirmware() {
      return this.firmwareFile && this.firmwareVersion && this.firmwareDeviceType;
    },
   // isFirmwareVersionValid() {
//   const version = this.firmwareVersion;
//   if (!version) return false;
//   const regex = /^\d+\.\d+\.\d+-(rel|beta|rc|dev)$/;
//   return regex.test(version);
// },
 
  },
  methods: {
    // When modal opens, fetch all resource lists
    onModalShow() {
      this.$emit('fetch-files');
      this.$emit('fetch-firmware');
      this.$emit('fetch-icons');
    },

    // Reset all state when modal closes
    resetAll() {
      // File
      this.activeFileTab = 0;
      this.fileToUpload = null;
      this.devicePath = "";
      this.uploading = false;
      // Firmware
      this.activeFirmwareTab = 0;
      this.firmwareFile = null;
      this.firmwareVersion = '';
      this.firmwareDeviceType = 'CC2';
      this.firmwareDescription = '';
      this.firmwareUploading = false;
      // Icon
      this.activeIconTab = 0;
      this.iconFile = null;
      this.iconName = "";
      this.iconDescription = "";
      this.iconUploading = false;
      // Outer tab
      this.outerTab = 0;
    },

    // Public method to open modal with optional initial tab
    openModal(initialTab = 0) {
      this.outerTab = initialTab;
      this.showModal = true;
    },

    // File methods
    onFileLoaded(content, name) {
      this.fileToUpload = { content, name };
    },
    uploadFile() {
      if (this.uploading) return;
      this.uploading = true;
      this.$emit('upload-file', {
        content: this.fileToUpload.content,
        name: this.fileToUpload.name,
        devicePath: this.devicePath,
        id: this.id,
        siteId: this.siteId
      }, () => {
        this.uploading = false;
        this.fileToUpload = null;
        this.devicePath = "";
        this.showModal = false; // close modal after success
      });
    },

    // Firmware methods
    onFirmwareFileLoaded(content, name) {
      this.firmwareFile = { content, name };
    },
    onWifiFirmwareFileLoaded(content, name) {
  this.wifiFirmwareFile = { content, name };
},
    uploadFirmware() {
      if (!this.canUploadFirmware) return;
      this.firmwareUploading = true;
      this.$emit('upload-firmware', {
        content: this.firmwareFile.content,
        name: this.firmwareFile.name,
        version: this.firmwareVersion,
        deviceType: this.firmwareDeviceType,
        description: this.firmwareDescription,
         wifiContent: this.wifiFirmwareFile?.content || null,
        wifiName: this.wifiFirmwareFile?.name || null,
        id: this.id,
        siteId: this.siteId
      }, () => {
        this.firmwareUploading = false;
        this.resetFirmwareForm();
        this.showModal = false;
      });
    },
    resetFirmwareForm() {
      this.firmwareFile = null;
      this.firmwareVersion = '';
      this.firmwareDeviceType = 'CC2';
      this.firmwareDescription = '';
         this.wifiFirmwareFile = null;
    },

    // Icon methods
    onIconFileLoaded(content, name) {
      this.iconFile = { content, name };
    },
    uploadIcon() {
      const baseName = this.iconName.trim();
      if (!this.iconFile || !baseName) return;
      const fileName = `${baseName}.pbm`;
      const devicePath = `imgs/${fileName}`;
      this.iconUploading = true;
      this.$emit('upload-icon', {
        content: this.iconFile.content,
        name: fileName,
        devicePath: devicePath,
        description: this.iconDescription,
        id: this.id,
        siteId: this.siteId
      }, () => {
        this.iconUploading = false;
        this.resetIconForm();
        this.showModal = false;
      });
    },
    resetIconForm() {
      this.iconFile = null;
      this.iconName = "";
      this.iconDescription = "";
    },

    // Utility
    formatDate(dateString) {
      if (!dateString) return '';
      const d = new Date(dateString);
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
    }
  }
};
</script>